/**
 * Service Worker Handler
 * 
 * This utility provides functions to interact with the service worker,
 * handle service worker messages, and manage offline/online state.
 */

import { detectUserLanguage } from '../utils/offlineSupport';

/**
 * Register the service worker
 * @returns {Promise} A promise that resolves when the service worker is registered
 */
export const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker
      .register('/service-worker.js')
      .then(registration => {
        console.log('Service Worker registered with scope:', registration.scope);
        return registration;
      })
      .catch(error => {
        console.error('Service Worker registration failed:', error);
        return null;
      });
  }
  return Promise.resolve(null);
};

/**
 * Check if a service worker update is available
 * @returns {Promise<boolean>} A promise that resolves to true if an update is available
 */
export const checkForServiceWorkerUpdate = () => {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready
      .then(registration => {
        return registration.update()
          .then(() => {
            if (registration.waiting) {
              return true; // Update is available
            }
            return false;
          });
      })
      .catch(() => false);
  }
  return Promise.resolve(false);
};

/**
 * Apply a service worker update
 * @returns {Promise<void>}
 */
export const applyServiceWorkerUpdate = () => {
  if ('serviceWorker' in navigator) {
    return navigator.serviceWorker.ready
      .then(registration => {
        if (registration.waiting) {
          // Send a message to the waiting service worker to skip waiting
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          return true;
        }
        return false;
      })
      .catch(() => false);
  }
  return Promise.resolve(false);
};

/**
 * Set up service worker message listeners
 * @param {Function} onMessage - Callback function to handle service worker messages
 * @returns {Function} A function to remove the event listener
 */
export const setupServiceWorkerMessages = (onMessage) => {
  if (!('serviceWorker' in navigator)) return () => {};

  const messageHandler = (event) => {
    if (event.data) {
      // Handle language check request
      if (event.data.type === 'CHECK_LANGUAGE') {
        const preferArabic = detectUserLanguage() === 'ar';
        
        // Send language preference back to service worker
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'LANGUAGE_PREFERENCE',
            preferArabic
          });
        }
        return;
      }
      
      // Pass other messages to the callback
      if (onMessage && typeof onMessage === 'function') {
        onMessage(event.data);
      }
    }
  };

  navigator.serviceWorker.addEventListener('message', messageHandler);
  
  // Return a function to remove the event listener
  return () => {
    navigator.serviceWorker.removeEventListener('message', messageHandler);
  };
};

/**
 * Handle offline redirect based on the current page
 * @param {string} offlinePage - The offline page URL
 */
export const handleOfflineRedirect = (offlinePage) => {
  if (!navigator.onLine && window.location.pathname !== offlinePage) {
    // Save the current URL to redirect back when online
    sessionStorage.setItem('lastOnlinePage', window.location.href);
    
    // Redirect to the offline page
    window.location.href = offlinePage;
  }
};

/**
 * Check if we should redirect back to the last online page
 * @returns {boolean} True if redirected
 */
export const checkRedirectBackOnline = () => {
  if (navigator.onLine) {
    const lastOnlinePage = sessionStorage.getItem('lastOnlinePage');
    if (lastOnlinePage) {
      sessionStorage.removeItem('lastOnlinePage');
      window.location.href = lastOnlinePage;
      return true;
    }
  }
  return false;
};

/**
 * Set up online/offline event listeners
 * @param {Function} onOnline - Callback function when going online
 * @param {Function} onOffline - Callback function when going offline
 * @returns {Function} A function to remove the event listeners
 */
export const setupOnlineOfflineListeners = (onOnline, onOffline) => {
  const handleOnline = () => {
    if (onOnline && typeof onOnline === 'function') {
      onOnline();
    }
    checkRedirectBackOnline();
  };

  const handleOffline = () => {
    if (onOffline && typeof onOffline === 'function') {
      onOffline();
    }
    
    // Get the appropriate offline page based on language
    const preferArabic = detectUserLanguage() === 'ar';
    const offlinePage = preferArabic ? '/offline.html' : '/offline-en.html';
    
    // Handle offline redirect
    handleOfflineRedirect(offlinePage);
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Return a function to remove the event listeners
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};